import React from 'react'
import { useNavigate } from 'react-router-dom'
import { ArrowBack, ArrowForward, DeleteOutline, ExpandMore, OpenInNew } from '@mui/icons-material'
import { Tooltip } from '@mui/material'
import { useDispatch } from '../../store'
import { api } from '../../function/axios'
import { formatDate } from '../../function/date'
import { getPriorityName } from '../../function/common'
import { ConfirmV2 } from '../ConfirmV2'
import { deleteTicket, editPriority, editStatus, toggleViewOne } from '../../store/support/actionCreator'
import { Ticket, TicketPriority, TicketStatus } from '../../store/support/reducer'
import { EnumTicketStatus } from '../../enum/statusTicket'
import { Subscriber } from '../../store/user/reducer'
import s from './SubscriberTicketsV2.module.css'

/*
 * Tickets tab of subscriber_v2 — the classic tab's list (SubscriberTickets)
 * in the v2 design: the same rows, the same actions (status, priority,
 * delete) and the same way out, "View in Support", which opens the ticket on
 * the support page. Ticket cards look like the user's own support list
 * (espy-dashboard container/support-v2): #id, title, type, status, date.
 *
 * Same endpoint as the classic tab: GET /ticket/:subscriberId.
 */

const LIMIT = 6

const STATUSES: TicketStatus[] = [EnumTicketStatus.OPEN, EnumTicketStatus.IN_PROGRESS, EnumTicketStatus.CLOSED]
const PRIORITIES: TicketPriority[] = ['low', 'medium', 'high']

const STATUS_TONE: Record<string, string> = {
  open: 'var(--danger)',
  'in-progress': 'var(--primary)',
  closed: 'var(--text3)',
}
const PRIORITY_TONE: Record<string, string> = {
  low: 'var(--text3)',
  medium: 'var(--warn)',
  high: 'var(--danger)',
}

const statusLabel = (v: string) => (v === 'in-progress' ? 'In progress' : v)

/* react-color keeps a type's colour as a hex string or an {r,g,b} / {hex} object */
function typeColor(c: unknown): string {
  if (typeof c === 'string') return c
  const o = c as any
  if (o?.hex) return o.hex
  if (o && Number.isFinite(o.r)) return `rgb(${o.r},${o.g},${o.b})`
  return 'var(--text3)'
}

/* a pill that is also the select — the value shows, a click changes it */
function PillSelect({ value, color, options, onChange, title }: {
  value: string
  color: string
  options: { value: string; label: string }[]
  onChange: (v: string) => void
  title: string
}) {
  return (
    <Tooltip title={title}>
      <select
        className={s.pillSelect}
        value={value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.value)}
        style={{ color, background: `color-mix(in srgb, ${color} 12%, transparent)`, borderColor: `color-mix(in srgb, ${color} 30%, transparent)` }}
      >
        {!value && <option value="">—</option>}
        {options.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </Tooltip>
  )
}

export function SubscriberTicketsV2({ data }: { data: Subscriber }) {
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [rows, setRows] = React.useState<Ticket[]>([])
  const [count, setCount] = React.useState<number | null>(null)
  const [offset, setOffset] = React.useState(0)
  const [loading, setLoading] = React.useState(false)
  const [open, setOpen] = React.useState<number | null>(null)
  const [deleteId, setDeleteId] = React.useState<number | null>(null)

  const load = React.useCallback(async () => {
    setLoading(true)
    try {
      const { data: res } = await api({ method: 'GET', url: `/ticket/${data.id}`, params: { offset, limit: LIMIT } })
      setRows(Array.isArray(res) ? res : (res?.list || []))
      setCount(Array.isArray(res) ? null : (res?.count ?? null))
    } catch (e) {
      setRows([])
    } finally {
      setLoading(false)
    }
  }, [data.id, offset])

  React.useEffect(() => { void load() }, [load])

  const pages = count != null ? Math.max(1, Math.ceil(count / LIMIT)) : null
  const page = Math.floor(offset / LIMIT) + 1
  const hasNext = pages != null ? page < pages : rows.length === LIMIT

  const patchRow = (id: number, patch: Partial<Ticket>) =>
    setRows((list) => list.map((t) => (t.id === id ? { ...t, ...patch } : t)))

  const viewInSupport = (ticket: Ticket) => {
    navigate('/support')
    dispatch(toggleViewOne(ticket))
  }

  return (
    <div className={s.panel}>
      <div className={s.head}>
        <h3>Subscriber tickets</h3>
        {count != null && <span className={s.count}>{count}</span>}
      </div>

      {!rows.length && <div className={s.empty}>{loading ? 'Loading…' : 'Sorry, no tickets from this subscriber'}</div>}

      <div className={s.list}>
        {rows.map((row) => {
          const isOpen = open === row.id
          return (
            <div key={row.id} className={`${s.tk} ${isOpen ? s.tkOn : ''}`}>
              <div className={s.row} role="button" tabIndex={0}
                onClick={() => setOpen(isOpen ? null : row.id)}
                onKeyDown={(e) => { if (e.key === 'Enter') setOpen(isOpen ? null : row.id) }}
              >
                <div className={s.main}>
                  <div className={s.tkTop}>
                    <span className={s.id}>#{row.id}</span>
                    <span className={s.title}>{row.title}</span>
                  </div>
                  <div className={s.tkMeta}>
                    {row.ticketType && (
                      <>
                        <span className={s.dot} style={{ background: typeColor(row.ticketType.color) }} />
                        <span className={s.type}>{row.ticketType.name}</span>
                      </>
                    )}
                    <span className={s.date}>{formatDate(row.createdTime, 'dd.MM.yyyy HH:mm')}</span>
                  </div>
                </div>

                <div className={s.side}>
                  <PillSelect
                    title="Status"
                    value={row.status}
                    color={STATUS_TONE[row.status] || 'var(--text3)'}
                    options={STATUSES.map((v) => ({ value: v, label: statusLabel(v).toUpperCase() }))}
                    onChange={(v) => { patchRow(row.id, { status: v as TicketStatus }); dispatch(editStatus(row.id, v, load)) }}
                  />
                  <PillSelect
                    title="Priority"
                    value={row.priority || ''}
                    color={PRIORITY_TONE[row.priority] || 'var(--text3)'}
                    options={PRIORITIES.map((v) => ({ value: v, label: String(getPriorityName(v)) }))}
                    onChange={(v) => { patchRow(row.id, { priority: v as TicketPriority }); dispatch(editPriority(row.id, v, load)) }}
                  />
                  <Tooltip title="View in Support">
                    <button type="button" className={s.iconBtn} onClick={(e) => { e.stopPropagation(); viewInSupport(row) }} aria-label="View in Support">
                      <OpenInNew sx={{ fontSize: 16 }} />
                    </button>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <button type="button" className={`${s.iconBtn} ${s.delBtn}`} onClick={(e) => { e.stopPropagation(); setDeleteId(row.id) }} aria-label="Delete ticket">
                      <DeleteOutline sx={{ fontSize: 17 }} />
                    </button>
                  </Tooltip>
                  <ExpandMore className={s.chev} sx={{ fontSize: 20, transform: isOpen ? 'rotate(180deg)' : 'none' }} />
                </div>
              </div>

              {isOpen && (
                <div className={s.more}>
                  <div className={s.summary}>{row.summary || <span className={s.muted}>No description</span>}</div>
                </div>
              )}
            </div>
          )
        })}
      </div>

      {(offset > 0 || hasNext) && (
        <div className={s.pager}>
          <span>Page {page}{pages ? ` of ${pages}` : ''}</span>
          <div className={s.pagerBtns}>
            <button type="button" className={s.iconBtn} disabled={offset === 0} onClick={() => setOffset((o) => Math.max(0, o - LIMIT))} aria-label="Previous page">
              <ArrowBack sx={{ fontSize: 16 }} />
            </button>
            <button type="button" className={s.iconBtn} disabled={!hasNext} onClick={() => setOffset((o) => o + LIMIT)} aria-label="Next page">
              <ArrowForward sx={{ fontSize: 16 }} />
            </button>
          </div>
        </div>
      )}

      {deleteId && (
        <ConfirmV2
          header={`Delete ticket #${deleteId}? The conversation goes with it.`}
          onYes={() => dispatch(deleteTicket(deleteId, load))}
          onClose={() => setDeleteId(null)}
        />
      )}
    </div>
  )
}
